import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { INavigationContent, IRootState } from '../../../types/store';
import { useEffect, useMemo, useState } from 'react';
import { getNavigationColumns } from './columns';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { deleteNavigationPage } from '../../../store/Reducers/NavigationSlice';
import { NavigationService } from '../../../api/services/navigationService';
import { showAlertDialog } from '../../../store/Reducers/AlertDialogSlice';
import { Box, Button } from '@mui/material';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { CreateUpdateNavigationDialog } from '../../Dialog/CreateUpdateNavigationDialog';
import { ViewNavigationDialog } from '../../Dialog/ViewNavigationDialog';
import { setPermissions } from '../../../store/Reducers/PermissionsSlice';
import { PermissionsService } from '../../../api/services/permissionsService';

export const NavigationDatagrid = ({ reload }: any) => {
  const { content } = useSelector((state: IRootState) => state.navigations);
  const [selectedNavigation, setSelectedNavigation] = useState<INavigationContent | null>(null);
  const [presetParentId, setPresetParentId] = useState<number | null>(null);
  const [isOpenCreateEditDialog, setIsOpenCreateEditDialog] = useState<boolean>(false);
  const [isOpenViewDialog, setIsOpenViewDialog] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onClickEdit = (navigation: INavigationContent) => {
    setSelectedNavigation(navigation);
    setPresetParentId(null);
    setIsOpenCreateEditDialog(true);
  };

  const onClickView = (navigation: INavigationContent) => {
    setSelectedNavigation(navigation);
    setIsOpenViewDialog(true);
  };

  const onClickAddSubpage = (navigation: INavigationContent) => {
    setSelectedNavigation(null);
    setPresetParentId(navigation.id);
    setIsOpenCreateEditDialog(true);
  };

  const onClickDelete = async (id: number) => {
    dispatch(
      showAlertDialog({
        title: 'Delete Navigation Page?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        onConfirm: () => {
          deleteNavigation(id);
        },
      }),
    );
  };

  const deleteNavigation = async (id: number) => {
    try {
      await NavigationService.deleteNavigation(id);
      dispatch(deleteNavigationPage(id));
      dispatch(showNotification({ message: 'Successfully deleted navigation page.' }));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to delete navigation page. ${e.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const columns = useMemo(() => {
    return getNavigationColumns(onClickDelete, onClickEdit, onClickView, onClickAddSubpage);
  }, []);

  const reloadPermissions = async () => {
    try {
      const permissions = await PermissionsService.getAllPermissions();
      dispatch(setPermissions(permissions));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve latest list of Permissions. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    reloadPermissions();
  }, []);

  return (
    <>
      <CommonDatagrid
        data={content}
        columns={columns}
        rowId={'id'}
        headerActionButtons={
          <Box display="flex" sx={{ gap: 2 }}>
            <RefreshButton onClick={reload} />
            <Button
              style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
              variant="contained"
              onClick={() => {
                setSelectedNavigation(null);
                setPresetParentId(null);
                setIsOpenCreateEditDialog(true);
              }}
            >
              Add Navigation Page
            </Button>
          </Box>
        }
        searchbarPlaceholder={'Search by page'}
        searchKey="title"
      />
      <CreateUpdateNavigationDialog
        selectedNavigation={selectedNavigation}
        presetParentId={presetParentId}
        isOpenDialog={isOpenCreateEditDialog}
        setIsOpenDialog={(open: boolean) => {
          setIsOpenCreateEditDialog(open);
          if (!open) {
            setSelectedNavigation(null);
            setPresetParentId(null);
          }
        }}
      />
      <ViewNavigationDialog
        navigation={selectedNavigation}
        isOpen={isOpenViewDialog}
        onClose={() => {
          setIsOpenViewDialog(false);
          setSelectedNavigation(null);
        }}
      />
    </>
  );
};
